import React, { FC, ReactElement } from 'react';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import InsetContainer from '@ad-scripts/presentation/components/common/containers/inset_container';
import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import NODES_MAPPER from 'src/presentation/components/pages/ivr/constants/nodesMapper';

type NodesCardListProps = {
  nodes: any;
};

const NodesCardList: FC<NodesCardListProps> = ({ nodes }) => {
  if (isEmpty(nodes)) {
    return null;
  }
  const nodeList = nodes.map((node, index: number) => {
    const nodeType = NODES_MAPPER[node.type];
    return (
      <NodeType data-aid={`node[${index}]`} key={node.id} nodeName={node.name} nodeType={node.name} {...nodeType} />
    );
  });

  return (
    <NodesSection>
      <NodesContainer data-aid="child-nodes-list">{nodeList}</NodesContainer>
    </NodesSection>
  );
};

type NodeType = {
  'data-aid': string;
  color: string;
  nodeName: string;
  icon: ReactElement;
  nodeType: NODE_TYPES;
  onSelect: (nodeType) => void;
  treeNodeLabel: string;
};

const NodeType = ({ 'data-aid': dataAid, color, nodeName, icon, treeNodeLabel }) => {
  const Icon = icon;
  return (
    <NodeBox className="node-box" data-aid={dataAid}>
      <LabelContainer color={color}>
        <Icon />
        <InfoLabel data-aid="title" color={color}>
          {treeNodeLabel}
        </InfoLabel>
      </LabelContainer>
      <InfoContainer>
        <InfoDescription data-aid="description">{nodeName.join(' ')}</InfoDescription>
      </InfoContainer>
    </NodeBox>
  );
};

const NodesSection = styled(InsetContainer)`
  height: 240px;
  margin: 16px 0;
  overflow: auto;
  width: 500px;
`;

const NodeBox = styled.div`
  align-items: flex-start;
  background: ${p => p.theme.colors.white};
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  height: 80px;
  padding: 10px 16px;
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    fill: ${p => p.color};
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const InfoLabel = styled.span`
  color: ${p => p.color};
  padding-left: 4px;
`;

const InfoDescription = styled.div`
  font-size: 13px;
  overflow: hidden;
  line-height: 162%;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  & > p {
    margin: 0;
  }
`;

const NodesContainer = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  width: 100%;

  & > ${NodeBox} {
    width: 50%;
    max-width: 45%;
    flex-grow: 1;

    margin: 10px;

    &:first-child,
    &:nth-child(2n + 1) {
      margin-left: 0;
    }

    &:nth-child(2n) {
      margin-right: 0;
    }
  }
`;

export default NodesCardList;
